/**
 * Inverted-index search for Guide Me tours.
 *
 * One `TourIndex` instance owns the postings table for an entire corpus
 * (every tour across every plugin). Built either at module load from
 * `TOUR_MANIFEST` (Phase 1) or rehydrated from a prebuilt JSON shipped via
 * `public/guide-me/` (Phase 2). The `search()` API is the only thing the UI
 * sees — implementation details (BM25 weights, stemmer choice, fuzzy
 * fallback) stay encapsulated.
 */

import { tokenize } from './tokenize';
import { stem } from './stem';
import { bm25Score } from './bm25';

/* ───────────────────────────── types ───────────────────────────── */

export interface TourDoc {
  readonly id: string;
  readonly pluginId: string;
  readonly title: string;
  readonly summary?: string;
  readonly route?: string;
  readonly pluginLabel?: string;
  /** Concatenated narration text — populated by the build script in Phase 2. */
  readonly narration?: string;
}

/** Field IDs are positional — keep this array stable across versions. */
const FIELDS = ['title', 'summary', 'pluginLabel', 'narration'] as const;
type FieldId = number;

const FIELD_BOOSTS: Readonly<Record<(typeof FIELDS)[number], number>> = {
  title: 3.0,
  summary: 1.5,
  pluginLabel: 0.6,
  // Narration is intentionally low: every tour that walks a screen tends to
  // mention the same domain words ("reset", "disable", "search") in passing.
  // We want narration to act as a tiebreaker when the title/summary signal
  // is weak, not to surface every tour that mentions the query in prose.
  narration: 0.4,
};

/**
 * Drop any hit whose score is less than `RELATIVE_SCORE_FLOOR × topScore`.
 * Without this, a strong query like "reset" still drags along every tour
 * whose narration happens to mention the word once. Tuned empirically:
 * 0.4 keeps near-duplicates of the leading hit and drops the long tail.
 */
const RELATIVE_SCORE_FLOOR = 0.4;

/** [docId, fieldId, tf] — tuple form keeps the serialized JSON ~40% smaller. */
type Posting = readonly [number, FieldId, number];

interface DocMeta {
  readonly id: string;
  readonly pluginId: string;
  readonly title: string;
  readonly summary?: string;
  readonly route?: string;
}

export interface SerializedIndex {
  readonly version: 1;
  readonly docs: ReadonlyArray<DocMeta>;
  /** [docId][fieldId] → token count after tokenize(). */
  readonly fieldLengths: ReadonlyArray<ReadonlyArray<number>>;
  /** [fieldId] → mean field length across docs that have a non-empty value. */
  readonly avgFieldLength: ReadonlyArray<number>;
  /** [fieldId] → number of docs that contain that field non-empty. */
  readonly fieldDocCount: ReadonlyArray<number>;
  readonly postings: Readonly<Record<string, ReadonlyArray<Posting>>>;
  readonly N: number;
}

export interface SearchHit {
  readonly docId: number;
  readonly tour: DocMeta;
  readonly score: number;
  readonly matchedTerms: ReadonlyArray<string>;
}

interface SearchOptions {
  readonly limit?: number;
  /**
   * Enable 1-edit fuzzy fallback for terms with zero exact postings. Off by
   * default at the index level — the UI passes `true` once the user has
   * stopped typing for a beat, so live typing doesn't pay the cost.
   */
  readonly fuzzy?: boolean;
  /**
   * Treat the last query term as a prefix (so "pass" matches "password").
   * The UI enables this for live-as-you-type and disables on submit.
   */
  readonly prefixLastTerm?: boolean;
}

/* ──────────────────────────── builder ───────────────────────────── */

interface BuildAccumulator {
  readonly docs: DocMeta[];
  readonly fieldLengths: number[][];
  readonly fieldLengthSum: number[];
  readonly fieldDocCount: number[];
  readonly postings: Map<string, Posting[]>;
}

function newAccumulator(): BuildAccumulator {
  return {
    docs: [],
    fieldLengths: [],
    fieldLengthSum: FIELDS.map(() => 0),
    fieldDocCount: FIELDS.map(() => 0),
    postings: new Map(),
  };
}

function indexField(
  acc: BuildAccumulator,
  docId: number,
  fieldId: FieldId,
  text: string | undefined,
) {
  const tokens = text ? tokenize(text) : [];
  acc.fieldLengths[docId][fieldId] = tokens.length;
  if (tokens.length === 0) return;

  acc.fieldLengthSum[fieldId] += tokens.length;
  acc.fieldDocCount[fieldId] += 1;

  // Count term frequencies for this (doc, field) pair.
  const tfMap = new Map<string, number>();
  for (const tok of tokens) tfMap.set(tok, (tfMap.get(tok) ?? 0) + 1);

  for (const [term, tf] of tfMap) {
    let list = acc.postings.get(term);
    if (!list) {
      list = [];
      acc.postings.set(term, list);
    }
    list.push([docId, fieldId, tf]);
  }
}

/* ──────────────────────────── class ────────────────────────────── */

export class TourIndex {
  private constructor(private readonly data: SerializedIndex) {}

  static build(docs: ReadonlyArray<TourDoc>): TourIndex {
    const acc = newAccumulator();

    docs.forEach((doc, docId) => {
      acc.docs.push({
        id: doc.id,
        pluginId: doc.pluginId,
        title: doc.title,
        summary: doc.summary,
        route: doc.route,
      });
      acc.fieldLengths.push(FIELDS.map(() => 0));
      indexField(acc, docId, 0, doc.title);
      indexField(acc, docId, 1, doc.summary);
      indexField(acc, docId, 2, doc.pluginLabel);
      indexField(acc, docId, 3, doc.narration);
    });

    const avgFieldLength = FIELDS.map((_f, i) =>
      acc.fieldDocCount[i] === 0 ? 0 : acc.fieldLengthSum[i] / acc.fieldDocCount[i],
    );

    const postingsObj: Record<string, ReadonlyArray<Posting>> = {};
    for (const [term, list] of acc.postings) postingsObj[term] = list;

    return new TourIndex({
      version: 1,
      docs: acc.docs,
      fieldLengths: acc.fieldLengths,
      avgFieldLength,
      fieldDocCount: acc.fieldDocCount,
      postings: postingsObj,
      N: acc.docs.length,
    });
  }

  static fromJSON(json: SerializedIndex): TourIndex {
    if (json.version !== 1) {
      throw new Error(`[guide-me/search] unsupported index version: ${json.version}`);
    }
    return new TourIndex(json);
  }

  toJSON(): SerializedIndex {
    return this.data;
  }

  get size(): number {
    return this.data.N;
  }

  /**
   * Score query against the corpus. Strategy:
   *   1. Tokenize the query the same way as the index (otherwise stems don't
   *      align — common port-bug source).
   *   2. For each query term: union postings across exact match, optional
   *      prefix expansion, optional 1-edit fuzzy expansion.
   *   3. Sum BM25(term, doc, field) × fieldBoost across all (term, field)
   *      pairs that contributed.
   *   4. Sort, slice to limit.
   */
  search(query: string, opts: SearchOptions = {}): SearchHit[] {
    const queryTerms = tokenize(query);
    if (queryTerms.length === 0) return [];

    const limit = opts.limit ?? 20;
    const docScores = new Map<number, number>();
    const docMatches = new Map<number, Set<string>>();

    queryTerms.forEach((term, idx) => {
      const isLast = idx === queryTerms.length - 1;
      const expansions = this.expandTerm(term, {
        prefix: opts.prefixLastTerm === true && isLast,
        fuzzy: opts.fuzzy === true,
      });
      if (expansions.length === 0) return;

      // Per query term, keep only this term's best contribution per doc —
      // prevents prefix and fuzzy expansions of the same query term from
      // double-counting against a single document.
      const perTermBest = new Map<number, number>();
      const perTermLabel = new Map<number, string>();

      for (const expanded of expansions) {
        const postings = this.data.postings[expanded];
        if (!postings) continue;
        const df = this.docFrequency(expanded);

        for (const [docId, fieldId, tf] of postings) {
          const fieldName = FIELDS[fieldId];
          const boost = FIELD_BOOSTS[fieldName] ?? 1;
          const fieldLen = this.data.fieldLengths[docId][fieldId];
          const avgLen = this.data.avgFieldLength[fieldId];
          const score = bm25Score(tf, df, this.data.N, fieldLen, avgLen) * boost;

          // Slight penalty for inexact (prefix/fuzzy) expansions so that
          // exact stem matches always win when both exist.
          const adjusted = expanded === term ? score : score * 0.7;

          // Sum across fields *for the same expansion*; pick best across
          // expansions.
          const docKey = docId * FIELDS.length + fieldId;
          const prevForExpansion = perTermBest.get(docKey) ?? 0;
          perTermBest.set(docKey, prevForExpansion + adjusted);
          perTermLabel.set(docId, expanded);
        }
      }

      // Collapse per-(doc,field) accumulator into per-doc, then merge into
      // global score.
      const perDoc = new Map<number, number>();
      for (const [docKey, s] of perTermBest) {
        const docId = Math.floor(docKey / FIELDS.length);
        perDoc.set(docId, (perDoc.get(docId) ?? 0) + s);
      }
      for (const [docId, s] of perDoc) {
        docScores.set(docId, (docScores.get(docId) ?? 0) + s);
        let bag = docMatches.get(docId);
        if (!bag) {
          bag = new Set();
          docMatches.set(docId, bag);
        }
        const label = perTermLabel.get(docId);
        if (label) bag.add(label);
      }
    });

    const hits: SearchHit[] = [];
    for (const [docId, score] of docScores) {
      hits.push({
        docId,
        tour: this.data.docs[docId],
        score,
        matchedTerms: Array.from(docMatches.get(docId) ?? []),
      });
    }
    hits.sort((a, b) => b.score - a.score);
    if (hits.length === 0) return hits;
    const floor = hits[0].score * RELATIVE_SCORE_FLOOR;
    const filtered = hits.filter((h) => h.score >= floor);
    return filtered.slice(0, limit);
  }

  /* ────────────────────────── internals ──────────────────────────── */

  private docFrequency(term: string): number {
    const list = this.data.postings[term];
    if (!list) return 0;
    // Distinct docs in the postings list.
    const seen = new Set<number>();
    for (const [docId] of list) seen.add(docId);
    return seen.size;
  }

  private expandTerm(
    term: string,
    opts: { prefix: boolean; fuzzy: boolean },
  ): string[] {
    const out: string[] = [];
    if (this.data.postings[term]) out.push(term);

    if (opts.prefix && term.length >= 2) {
      // Linear scan is fine here — postings keys are at most a few thousand
      // for our corpus, and the search runs on user input cadence.
      for (const key of Object.keys(this.data.postings)) {
        if (key !== term && key.startsWith(term)) out.push(key);
        if (out.length > 32) break; // cap to avoid pathological prefixes like "a"
      }
    }

    if (opts.fuzzy && out.length === 0) {
      for (const key of Object.keys(this.data.postings)) {
        if (Math.abs(key.length - term.length) > 1) continue;
        if (editDistanceAtMost1(term, key)) out.push(key);
        if (out.length > 8) break;
      }
    }

    return out;
  }
}

/* ────────────────────────── helpers ───────────────────────────── */

/**
 * Returns true if Levenshtein distance between a and b is at most 1.
 * Tight loop, avoids allocating a DP matrix for the common case.
 */
function editDistanceAtMost1(a: string, b: string): boolean {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) return false;

  if (la === lb) {
    let diffs = 0;
    for (let i = 0; i < la; i++) {
      if (a[i] !== b[i] && ++diffs > 1) return false;
    }
    return true;
  }

  // Lengths differ by 1 — try a single-insertion alignment.
  const [shorter, longer] = la < lb ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let edited = false;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] === longer[j]) {
      i++;
      j++;
    } else {
      if (edited) return false;
      edited = true;
      j++;
    }
  }
  return true;
}

/* convenience re-exports for callers that want lower-level pieces */
export { tokenize, stem };
